// @ts-check

import { getCurrentPadding, ZOOM_SETTINGS } from "./mapConstants.js";

/**
 * Mapbox zoom and bounds utilities.
 * Handles calculating bounds, zooming to features, and resetting view.
 */

/**
 * Normalize longitude to handle date line crossing.
 * Converts longitudes to 0-360 range if features cross the date line.
 * @param {number[]} longitudes - Array of longitude values
 * @returns {{normalized: number[], crossesDateLine: boolean}} Normalized longitudes and crossing flag
 */
function normalizeLongitudes(longitudes) {
  // Check if we cross the date line by looking for large longitude differences
  const minLng = Math.min(...longitudes);
  const maxLng = Math.max(...longitudes);
  const span = maxLng - minLng;

  // If span is > 180, we likely cross the date line
  const crossesDateLine = span > 180;

  if (crossesDateLine) {
    // Convert negative longitudes to 0-360 range
    const normalized = longitudes.map((lng) => (lng < 0 ? lng + 360 : lng));
    return { normalized, crossesDateLine: true };
  }

  return { normalized: longitudes, crossesDateLine: false };
}

/**
 * Calculate bounding box from GeoJSON FeatureCollection.
 * Handles date line crossing for Pacific regions.
 * @param {Object} geoJson - GeoJSON FeatureCollection
 * @returns {[[number, number], [number, number]] | null} Bounding box [[west, south], [east, north]] or null if no features
 */
export function calculateMapBounds(geoJson) {
  if (!geoJson || !geoJson.features || geoJson.features.length === 0) {
    return null;
  }

  const allLongitudes = [];
  const allLatitudes = [];

  // Collect all coordinates
  geoJson.features.forEach((feature) => {
    if (!feature.geometry || !feature.geometry.coordinates) return;

    const { type, coordinates } = feature.geometry;

    if (type === "Point") {
      const [lng, lat] = coordinates;
      allLongitudes.push(lng);
      allLatitudes.push(lat);
    } else if (type === "Polygon") {
      coordinates.forEach((ring) => {
        ring.forEach(([lng, lat]) => {
          allLongitudes.push(lng);
          allLatitudes.push(lat);
        });
      });
    } else if (type === "MultiPolygon") {
      coordinates.forEach((polygon) => {
        polygon.forEach((ring) => {
          ring.forEach(([lng, lat]) => {
            allLongitudes.push(lng);
            allLatitudes.push(lat);
          });
        });
      });
    } else if (type === "LineString") {
      coordinates.forEach(([lng, lat]) => {
        allLongitudes.push(lng);
        allLatitudes.push(lat);
      });
    }
  });

  if (allLongitudes.length === 0 || allLatitudes.length === 0) {
    return null;
  }

  // Handle date line crossing
  const { normalized, crossesDateLine } = normalizeLongitudes(allLongitudes);

  let minLng = Math.min(...normalized);
  let maxLng = Math.max(...normalized);
  const minLat = Math.min(...allLatitudes);
  const maxLat = Math.max(...allLatitudes);

  // Convert back to -180 to 180 range if we normalized
  if (crossesDateLine) {
    // Keep the western bound in 0-360 range, but ensure east bound wraps correctly
    if (maxLng > 180) {
      // If max is > 180, we want to use it as-is for the eastern bound
      // But adjust the western bound to be negative if needed
      if (minLng > 180) {
        minLng = minLng - 360;
      }
    }
  }

  return [
    [minLng, minLat], // Southwest corner
    [maxLng, maxLat] // Northeast corner
  ];
}

/**
 * Fit map to calculated bounds with responsive padding.
 * @param {import('mapbox-gl').Map} map - Mapbox map instance
 * @param {[[number, number], [number, number]]} bounds - Bounding box
 * @param {Object} [customPadding] - Optional custom padding (overrides responsive padding)
 * @param {boolean} [animate=false] - Whether to animate the transition
 */
export function fitMapToInitialBounds(
  map,
  bounds,
  customPadding = null,
  animate = false
) {
  if (!map || !bounds) return;

  const padding = customPadding || getCurrentPadding();

  map.fitBounds(bounds, {
    padding,
    animate,
    duration: animate ? ZOOM_SETTINGS.duration : 0
  });
}

/**
 * Zoom to a specific feature (island, region, or route).
 * @param {import('mapbox-gl').Map} map - Mapbox map instance
 * @param {Object} feature - GeoJSON feature
 * @param {Object} [options] - Zoom options
 * @param {number} [options.zoom] - Target zoom level (default: ZOOM_SETTINGS.islandZoom)
 * @param {number} [options.duration] - Animation duration (default: ZOOM_SETTINGS.duration)
 * @param {Object} [options.padding] - Custom padding
 */
export function zoomToFeature(map, feature, options = {}) {
  if (!map || !feature || !feature.geometry) return;

  const { type, coordinates } = feature.geometry;
  const zoom = options.zoom || ZOOM_SETTINGS.islandZoom;
  const duration = options.duration || ZOOM_SETTINGS.duration;

  if (type === "Point") {
    // For points, fly to center
    map.flyTo({
      center: coordinates,
      zoom,
      duration,
      curve: ZOOM_SETTINGS.curve,
      speed: ZOOM_SETTINGS.speed
    });
  } else if (type === "Polygon" || type === "MultiPolygon") {
    // For polygons, calculate bounds and fit
    const featureBounds = calculateMapBounds({
      type: "FeatureCollection",
      features: [feature]
    });

    if (featureBounds) {
      const padding = options.padding || getCurrentPadding();
      map.fitBounds(featureBounds, {
        padding,
        duration,
        maxZoom: zoom
      });
    }
  } else if (type === "LineString") {
    // For lines, calculate bounds and fit
    const featureBounds = calculateMapBounds({
      type: "FeatureCollection",
      features: [feature]
    });

    if (featureBounds) {
      const padding = options.padding || getCurrentPadding();
      map.fitBounds(featureBounds, {
        padding,
        duration,
        maxZoom: zoom
      });
    }
  }
}

/**
 * Reset map to initial view.
 * @param {import('mapbox-gl').Map} map - Mapbox map instance
 * @param {[[number, number], [number, number]]} initialBounds - Initial bounding box
 * @param {Object} [customPadding] - Optional custom padding
 */
export function resetMapView(map, initialBounds, customPadding = null) {
  if (!map || !initialBounds) return;

  fitMapToInitialBounds(map, initialBounds, customPadding, true);
}

/**
 * Get feature coordinates for zoom operations.
 * Helper to extract coordinates from various feature types.
 * @param {Object} feature - GeoJSON feature or feature properties
 * @returns {[number, number] | null} Coordinates [lng, lat] or null
 */
export function getFeatureCoordinates(feature) {
  if (!feature) return null;

  // If it's a full GeoJSON feature
  if (feature.geometry && feature.geometry.coordinates) {
    const { type, coordinates } = feature.geometry;

    if (type === "Point") {
      return coordinates;
    } else if (type === "Polygon" && coordinates.length > 0) {
      // Return first coordinate of first ring
      return coordinates[0][0];
    } else if (type === "MultiPolygon" && coordinates.length > 0) {
      // Return first coordinate of first polygon's first ring
      return coordinates[0][0][0];
    }
  }

  return null;
}
